import {
  Dropdown2,
  type Dropdown2Group,
  type Dropdown2Option,
} from "@/components/ui/dropdown2"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowRight, RefreshCcw } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"
import { COLUMN_MAPPING_OPTIONS } from "../../api/columnMappingConstants"
import { McPill } from "../shared/McPill"
import { PkBadge } from "../shared/PkBadge"
import { SectionLabel } from "../shared/SectionLabel"
import {
  COLUMN_STATE_TRIGGER,
  countAssignments,
  getColumnState,
  groupColumnsBySheet,
  isPrimaryKeyAssignment,
  scopedColumnKey,
  SKIPPED_ASSIGNMENT,
  SKIPPED_COLUMN_CLASS,
  type ColumnState,
  type MappingColumn,
} from "./mappingUtils"

// `additional_attributes` is folded into the parent entity as a "Custom attribute" option.
const ENTITY_TO_ATTR_FIELD: Record<string, string> = {
  suppliers: "on_supplier",
  articles: "on_article",
  products: "on_product",
  customers: "on_customer",
}

const ATTR_FIELD_TO_ENTITY: Record<string, string> = Object.fromEntries(
  Object.entries(ENTITY_TO_ATTR_FIELD).map(([entity, attr]) => [attr, entity]),
)

/**
 * Builds the dropdown's field groups from `COLUMN_MAPPING_OPTIONS`, resolving
 * every `labelKey`/`descriptionKey` via `translate`. Built once per render in
 * `MappingColumnList` (which owns `useTranslation`) and passed down to each
 * `ColumnRow` rather than recomputed per row.
 */
function buildFieldGroups(
  translate: (key: TranslationKeys) => string,
): Dropdown2Group[] {
  return COLUMN_MAPPING_OPTIONS.filter(
    (table) => table.table !== "additional_attributes",
  ).map((table) => {
    const options: Dropdown2Option[] = table.fields.map((field) => ({
      value: `${table.table}:${field.value}`,
      label: translate(field.labelKey),
      badge: field.primary_key ? <PkBadge /> : undefined,
    }))
    const attrField = ENTITY_TO_ATTR_FIELD[table.table]
    if (attrField) {
      options.push({
        value: `additional_attributes:${attrField}`,
        label: translate("MC_MD_MAP_CUSTOM_ATTRIBUTE_LABEL"),
        description: translate("MC_MD_MAP_CUSTOM_ATTRIBUTE_DESC"),
      })
    }
    return {
      key: table.table,
      label: translate(table.labelKey),
      description: translate(table.descriptionKey),
      options,
    }
  })
}

// ─── Column row ───────────────────────────────────────────────────────────────

function ColumnRow({
  column,
  assignment,
  active,
  fieldGroups,
  leadingOptions,
  onSelect,
  onAssignmentChange,
  onReset,
}: {
  column: MappingColumn
  assignment: string | null
  active: boolean
  fieldGroups: Dropdown2Group[]
  /** Listed with "No assignment", above the field groups ("Skip column"). */
  leadingOptions: Dropdown2Option[]
  onSelect: () => void
  onAssignmentChange: (value: string | null) => void
  onReset: () => void
}) {
  const { translate } = useTranslation()
  const state: ColumnState = getColumnState(assignment, column.aiAssigned)

  // Pin the selected entity's group to the top of the dropdown.
  const groups = useMemo(() => {
    if (!assignment) return fieldGroups
    const [table, field] = assignment.split(":")
    const targetKey =
      table === "additional_attributes" && field
        ? (ATTR_FIELD_TO_ENTITY[field] ?? table)
        : table
    const selected = fieldGroups.find((g) => g.key === targetKey)
    if (!selected) return fieldGroups
    return [selected, ...fieldGroups.filter((g) => g.key !== targetKey)]
  }, [assignment, fieldGroups])

  return (
    <div
      data-column-ref={column.columnRef}
      data-sheet-name={column.sheetName ?? ""}
      onClick={onSelect}
      className={cn(
        "flex cursor-pointer items-center gap-3 border-b border-l-2 px-3 py-2 transition-colors",
        active
          ? "border-l-primary bg-primary/10"
          : "border-l-transparent hover:bg-primary/4",
        state === "skipped" && SKIPPED_COLUMN_CLASS,
      )}
    >
      <span className="font-mono flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-card text-xs text-muted-foreground">
        {column.columnRef}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate text-sm text-foreground">
          {column.columnName}
        </span>
        {isPrimaryKeyAssignment(assignment) && <PkBadge />}
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />

      <Dropdown2
        value={assignment}
        onChange={(value) => {
          onSelect()
          onAssignmentChange(value)
        }}
        groups={groups}
        leadingOptions={leadingOptions}
        noSelectionLabel={translate("MC_MD_MAP_NO_ASSIGNMENT")}
        searchPlaceholder={translate("MC_MD_MAP_FIELD_SEARCH_PLACEHOLDER")}
        emptyText={translate("MC_MD_MAP_FIELD_SEARCH_EMPTY")}
        triggerWidth="220px"
        popoverWidth="280px"
        triggerClassName={cn(
          COLUMN_STATE_TRIGGER[state],
          active && "ring-2 ring-primary/40",
        )}
        groupHeadingClassName="[&_[cmdk-group-heading]]:font-mono **:[[cmdk-group-heading]]:text-[10px] **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-widest **:[[cmdk-group-heading]]:text-yellow1"
      />

      <button
        type="button"
        aria-label={translate("MC_MD_MAP_RESET_ARIA")}
        onClick={(event) => {
          event.stopPropagation()
          onSelect()
          onReset()
        }}
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
          // A skip is always a change: the AI never suggests one.
          state !== "modified" && state !== "skipped" && "invisible",
        )}
      >
        <RefreshCcw className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ─── List ───────────────────────────────────────────────────────────────────

interface MappingColumnListProps {
  columns: MappingColumn[]
  activeSheetName: string | null
  activeColumnRef: string | null
  onActiveColumnChange: (sheetName: string | null, columnRef: string) => void
  /** Keyed by `scopedColumnKey`, not the bare column name (T1). */
  resolveAssignment: (
    columnKey: string,
    aiAssigned: string | null,
  ) => string | null
  onAssignmentChange: (columnKey: string, value: string | null) => void
  onResetAssignment: (columnKey: string, aiAssigned: string | null) => void
}

/** Left pane: one row per spreadsheet column with its target-field selector. */
export function MappingColumnList({
  columns,
  activeSheetName,
  activeColumnRef,
  onActiveColumnChange,
  resolveAssignment,
  onAssignmentChange,
  onResetAssignment,
}: MappingColumnListProps) {
  const { translate } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const counts = countAssignments(
    columns.map((column) =>
      resolveAssignment(
        scopedColumnKey(column.sheetName, column.columnName),
        column.aiAssigned,
      ),
    ),
  )
  const allMapped = counts.unmapped === 0

  const fieldGroups = useMemo(() => buildFieldGroups(translate), [translate])
  const leadingOptions = useMemo<Dropdown2Option[]>(
    () => [
      { value: SKIPPED_ASSIGNMENT, label: translate("MC_MD_MAP_SKIP_COLUMN") },
    ],
    [translate],
  )

  const sheetGroups = useMemo(() => groupColumnsBySheet(columns), [columns])
  const isMultiSheet = sheetGroups.length > 1

  useEffect(() => {
    if (!activeColumnRef) return
    // Sheet names come from customer-uploaded workbooks and may legally
    // contain characters (a double quote, a backslash) that break out of
    // the attribute-selector string — Excel only forbids : \ / ? * [ ].
    // CSS.escape() keeps an unusual name from throwing a SyntaxError here.
    scrollRef.current
      ?.querySelector(
        `[data-sheet-name="${CSS.escape(activeSheetName ?? "")}"][data-column-ref="${CSS.escape(activeColumnRef)}"]`,
      )
      ?.scrollIntoView({ block: "nearest" })
  }, [activeSheetName, activeColumnRef])

  return (
    <div ref={scrollRef} className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b bg-grey1 px-4 py-3">
        <SectionLabel>{translate("MC_MD_MAP_COLUMN_TO_FIELD")}</SectionLabel>
        <div className="flex items-center gap-1.5">
          {counts.skipped > 0 && (
            <McPill tone="muted">
              {translate("MC_MD_MAP_SKIPPED_COUNT", { count: counts.skipped })}
            </McPill>
          )}
          <McPill tone={allMapped ? "success" : "muted"}>
            {translate("MC_MD_MAP_MAPPED", {
              assigned: counts.mapped,
              total: columns.length - counts.skipped,
            })}
          </McPill>
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        {sheetGroups.map((group) => (
          <div key={group.sheetName ?? "__default__"}>
            {isMultiSheet && (
              <div className="font-mono sticky top-0 z-10 border-b bg-grey1/90 px-3 py-1.5 text-[10px] font-semibold tracking-widest text-muted-foreground uppercase backdrop-blur-xs">
                {group.sheetName}
              </div>
            )}
            {group.columns.map((column) => {
              const key = scopedColumnKey(column.sheetName, column.columnName)
              return (
                <ColumnRow
                  key={`${column.sheetName ?? ""}:${column.columnRef}`}
                  column={column}
                  assignment={resolveAssignment(key, column.aiAssigned)}
                  active={
                    column.columnRef === activeColumnRef &&
                    column.sheetName === activeSheetName
                  }
                  fieldGroups={fieldGroups}
                  leadingOptions={leadingOptions}
                  onSelect={() =>
                    onActiveColumnChange(column.sheetName, column.columnRef)
                  }
                  onAssignmentChange={(value) => onAssignmentChange(key, value)}
                  onReset={() => onResetAssignment(key, column.aiAssigned)}
                />
              )
            })}
          </div>
        ))}
      </ScrollArea>
    </div>
  )
}
